import React, { useCallback, useEffect, useState } from 'react';
import {
  Autocomplete,
  Button,
  Container,
  Grid,
  IconButton,
  MenuItem,
  Select,
  Stack,
  tableCellClasses,
  TextField,
  Tooltip,
  Typography,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material';
import { Box, styled } from '@mui/system';
import { useForm, useFieldArray } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { AiFillCloseCircle, AiOutlineMinus } from 'react-icons/ai';
import { useDispatch } from 'react-redux';
import { MdModeEdit, MdOutlineAdd, MdOutlineUpdate } from 'react-icons/md';
import { useLocation } from 'react-router-dom';
import BrudCrumbs from '../../components/BreadCrumbs';
import Page from '../../components/Page';
import PageTitle from '../../components/PageHeading';
import useLoginSelector from '../../store/Slice/Login/LoginSelector';
import useGlobalSelector from '../../store/Slice/Global/GlobalSelector';
import {
  getBankDropdownData,
  getMasterLoanTypes,
  saveApiData
} from '../../store/Slice/Global/GlobalSlice';
import useOnboardingSelector from '../../store/Slice/Onboarding/OnboardingSelector';
import { getOnboardingShowData } from '../../store/Slice/Onboarding/OnboardingSlice';
import { OnboardingApi } from '../../utils/Apis';
import CustomInput from '../../components/CustomInput';
import CustomSelect from '../../components/CustomSelect';
import { setApiError } from '../../store/Slice/Error/ErrorSlice';

const validationSchema = yup.object().shape({
  partnerCommission: yup
    .array()
    .of(
      yup
        .object()
        .shape({
          bankId: yup.string().required('Please select bank name'),
          loanTypeId: yup.string().required('Please select loan type'),
          companyCommition: yup
            .number()
            .typeError('Type company commission %')
            .required('Type company commission %')
            .min(0, 'Commission cannot be negative')
            .max(100, 'Commission cannot exceed 100%'),
          partnerCommition: yup
            .number()
            .typeError('Type partner share %')
            .required('Type partner share %')
            .min(0, 'Partner share cannot be negative')
            .max(100, 'Partner share cannot exceed 100%')
        })
        .test(
          'total-not-greater-than-100',
          'Sum of Company Commission & Partner Share should not be greater than 100%',
          function (item) {
            const total = Number(item.companyCommition || 0) + Number(item.partnerCommition || 0);
            if (total > 100) {
              return this.createError({
                path: `${this.path}.partnerCommition`,
                message: 'Sum of Company Commission & Partner Share should not be greater than 100%'
              });
            }
            return true;
          }
        )
    )
    .min(1, 'At least one partner commission entry is required')
});

const StyledTableCell = styled(TableCell)(() => ({
  [`&.${tableCellClasses.head}`]: {
    fontSize: 13,
    padding: '8px 12px',
    fontWeight: 'bold',
    color: '#13519d',
    backgroundColor: '#ffffff'
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 12,
    padding: '8px 12px'
  }
}));

function SetCommissionOnBoarding() {
  const [editcomm, setEditcomm] = useState('');
  const [autosugval, setAutosugval] = useState({
    loanTypeId: '',
    companyCommition: '',
    partnerCommition: ''
  });

  const dispatch = useDispatch();
  const location = useLocation();
  const { rowId } = location?.state || {};
  const { LoginData } = useLoginSelector();
  const { OnboardingShowCommision } = useOnboardingSelector();
  const { BankDrop, MasterLoanTypeDrop } = useGlobalSelector();

  const newBankDrop = [{ label: 'All', value: 0 }, ...BankDrop] || [];
  const newMasterLoanTypeDrop = [{ label: 'All', value: 0 }, ...MasterLoanTypeDrop] || [];

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors }
  } = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      partnerCommission: [
        {
          bankId: '',
          loanTypeId: '',
          companyCommition: '',
          partnerCommition: ''
        }
      ]
    }
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'partnerCommission'
  });

  const handleAddRow = () => {
    append({
      bankId: '',
      loanTypeId: '',
      companyCommition: '',
      partnerCommition: ''
    });
  };

  const handleRemoveRow = (index) => remove(index);

  const firedApiPartnerUpdate = (commitionId, index) => {
    let isCond = true;
    if (
      OnboardingShowCommision?.[index].companyCommition !== '' &&
      OnboardingShowCommision?.[index].partnerCommition !== ''
    ) {
      const total = Number(autosugval.companyCommition) + Number(autosugval.partnerCommition);
      if (total > 100) {
        isCond = false;
        dispatch(
          setApiError('Sum of Company Commission & Partner Share should not be greater then 100%')
        );
        autosugval.companyCommition = 0;
        autosugval.partnerCommition = 0;
        return true;
      }
    }

    if (isCond) {
      (async () => {
        const credential = {
          companyId: LoginData?.companyId,
          partnerId: rowId?.partnerId || rowId,
          partnerCommissions: [
            {
              companyCommition: autosugval?.companyCommition,
              partnerCommition: autosugval?.partnerCommition,
              bankId: autosugval?.bankId,
              loanTypeId: autosugval?.loanTypeId,
              commitionId: commitionId
            }
          ]
        };
        const result = await dispatch(saveApiData(OnboardingApi?.onboardSetCommission, credential));
        if (result?.status === 'success') {
          getOnboardingShowDataApi();
          removecommission();
        }
      })();
    }
  };

  const editcommission = (data) => {
    setEditcomm(data?.commitionId);
    setAutosugval({
      bankId: data?.bankId,
      loanTypeId: data?.loanTypeId,
      companyCommition: data?.companyCommition,
      partnerCommition: data?.partnerCommition
    });
  };

  const removecommission = () => {
    setEditcomm('');
    setAutosugval({});
  };

  const onSubmit = useCallback(
    async (data) => {
      const credential = {
        companyId: LoginData?.companyId,
        partnerId: rowId?.partnerId || rowId,
        partnerCommissions: data?.partnerCommission
      };
      const result = await dispatch(saveApiData(OnboardingApi?.onboardSetCommission, credential));
      if (result?.status === 'success') {
        getOnboardingShowDataApi();
        reset();
      }
    },
    [LoginData, rowId, dispatch, reset]
  );

  const getBankDropApi = useCallback(() => {
    if (!LoginData) return;
    const data = { companyId: LoginData?.companyId, userId: LoginData?.userId };
    dispatch(getBankDropdownData(data));
  }, [dispatch, LoginData]);

  const getOnboardingShowDataApi = useCallback(() => {
    if (!LoginData && !rowId) return;
    const data = {
      companyId: LoginData?.companyId,
      partnerId: rowId?.partnerId || rowId,
      sortOrder: 'asc'
    };
    dispatch(getOnboardingShowData(data));
  }, [rowId, LoginData, dispatch]);

  useEffect(() => {
    getBankDropApi();
  }, [getBankDropApi]);

  useEffect(() => {
    getOnboardingShowDataApi();
  }, [getOnboardingShowDataApi]);

  useEffect(() => {
    dispatch(getMasterLoanTypes());
  }, [dispatch]);

  return (
    <Page title="Partner Management System || Set Commission">
      <Container maxWidth="xl">
        <Box pt={3} pb={3} mb={4}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            className="pageHeading"
            sx={{ mb: 3 }}
          >
            <PageTitle info="Set Commission" />
            <Box>
              <BrudCrumbs />
            </Box>
          </Stack>

          {/* Form Card */}
          <Card
            sx={{
              p: 3.5,
              borderRadius: '16px',
              border: '1px solid #e2e8f0',
              boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
              mb: 3
            }}
          >
            <Typography sx={{ fontSize: 16, fontWeight: 700, color: '#13519d', mb: 2.5 }}>
              Set Commission Rules
            </Typography>

            <form id="setSubmitForm" onSubmit={handleSubmit(onSubmit)}>
              {fields.map((item, index) => (
                <Grid container alignItems="center" key={item.id} spacing={2} sx={{ mb: 2 }}>
                  <Grid item xs={12} sm={6} md={3}>
                    <CustomSelect
                      name={`partnerCommission.${index}.bankId`}
                      label="Bank Name"
                      control={control}
                      errors={errors?.partnerCommission?.[index]}
                      options={newBankDrop}
                      placeholder="Select Bank"
                      isRequired
                      isMultipleError
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={3}>
                    <CustomSelect
                      name={`partnerCommission.${index}.loanTypeId`}
                      label="Loan Type"
                      control={control}
                      errors={errors?.partnerCommission?.[index]}
                      options={newMasterLoanTypeDrop}
                      placeholder="Select Loan Type"
                      isRequired
                      isMultipleError
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={2.5}>
                    <CustomInput
                      name={`partnerCommission.${index}.companyCommition`}
                      label="Company Commission %"
                      type="number"
                      control={control}
                      errors={errors?.partnerCommission?.[index]}
                      placeholder="e.g. 1.5"
                      isRequired
                      isMultipleError
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={2.5}>
                    <CustomInput
                      name={`partnerCommission.${index}.partnerCommition`}
                      label="Partner Share %"
                      type="number"
                      control={control}
                      errors={errors?.partnerCommission?.[index]}
                      placeholder="e.g. 0.5"
                      isRequired
                      isMultipleError
                    />
                  </Grid>

                  <Grid
                    item
                    xs={12}
                    md={1}
                    sx={{ display: 'flex', gap: 0.5, mt: { xs: 0, md: 2.5 } }}
                  >
                    <Tooltip title="Add Row" arrow>
                      <IconButton
                        onClick={handleAddRow}
                        sx={{
                          bgcolor: 'rgba(19, 81, 157, 0.08)',
                          color: '#13519d',
                          borderRadius: '8px',
                          '&:hover': { bgcolor: 'rgba(19, 81, 157, 0.16)' }
                        }}
                      >
                        <MdOutlineAdd size={18} />
                      </IconButton>
                    </Tooltip>

                    {fields.length > 1 && (
                      <Tooltip title="Remove Row" arrow>
                        <IconButton
                          onClick={() => handleRemoveRow(index)}
                          sx={{
                            bgcolor: 'rgba(239, 68, 68, 0.08)',
                            color: '#ef4444',
                            borderRadius: '8px',
                            '&:hover': { bgcolor: 'rgba(239, 68, 68, 0.16)' }
                          }}
                        >
                          <AiOutlineMinus size={18} />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Grid>
                </Grid>
              ))}
            </form>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', pt: 2 }}>
              <Button
                variant="contained"
                type="submit"
                form="setSubmitForm"
                sx={{
                  bgcolor: '#13519d',
                  px: 4,
                  py: 1,
                  fontWeight: 600,
                  borderRadius: '8px',
                  textTransform: 'none',
                  boxShadow: '0 4px 12px rgba(19, 81, 157, 0.25)',
                  '&:hover': { bgcolor: '#0d3d75' }
                }}
              >
                Submit & Close
              </Button>
            </Box>
          </Card>

          {/* Commission Log Card */}
          <Card
            sx={{
              borderRadius: '16px',
              border: '1px solid #e2e8f0',
              boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
              overflow: 'hidden'
            }}
          >
            <Box sx={{ p: 2.5, bgcolor: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
              <Typography sx={{ fontSize: 16, fontWeight: 700, color: '#13519d' }}>
                Commission Log
              </Typography>
            </Box>

            <TableContainer sx={{ minWidth: 800, overflow: 'auto' }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <StyledTableCell style={{ width: '220px' }}>Bank</StyledTableCell>
                    <StyledTableCell style={{ width: '230px' }}>Loan Type</StyledTableCell>
                    <StyledTableCell style={{ width: '200px' }}>Our Commission %</StyledTableCell>
                    <StyledTableCell style={{ width: '200px' }}>Partner Share %</StyledTableCell>
                    <StyledTableCell style={{ width: '150px' }}>Modified On</StyledTableCell>
                    <StyledTableCell align="center" style={{ width: '120px' }}>
                      Action
                    </StyledTableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {OnboardingShowCommision?.map((item, index) => (
                    <TableRow
                      key={index}
                      sx={{
                        '&:nth-of-type(even)': { backgroundColor: '#fafbfc' },
                        '&:hover': { backgroundColor: 'rgba(19, 81, 157, 0.04)' }
                      }}
                    >
                      <StyledTableCell>
                        {editcomm !== item.commitionId ? (
                          newBankDrop?.find((b) => b.value === item.bankId)?.label || '-'
                        ) : (
                          <Autocomplete
                            value={
                              autosugval.bankId
                                ? newBankDrop.find((opt) => opt.value === autosugval.bankId) || null
                                : null
                            }
                            options={newBankDrop || []}
                            getOptionLabel={(option) => option.label || ''}
                            size="small"
                            renderInput={(params) => (
                              <TextField {...params} fullWidth placeholder="Select Bank" />
                            )}
                            onChange={(e, newValue) => {
                              setAutosugval({
                                ...autosugval,
                                bankId: newValue ? newValue.value : '',
                                bankLabel: newValue ? newValue.label : ''
                              });
                            }}
                          />
                        )}
                      </StyledTableCell>
                      <StyledTableCell>
                        {editcomm !== item.commitionId ? (
                          newMasterLoanTypeDrop?.find((l) => l.value === item.loanTypeId)?.label ||
                          '-'
                        ) : (
                          <Select
                            fullWidth
                            size="small"
                            value={autosugval.loanTypeId}
                            onChange={(e) => {
                              setAutosugval({ ...autosugval, loanTypeId: e.target.value });
                            }}
                          >
                            <MenuItem value=" " disabled>
                              Select Loan Type
                            </MenuItem>
                            {newMasterLoanTypeDrop?.map((lt) => (
                              <MenuItem value={lt.value} key={lt.value}>
                                {lt.label}
                              </MenuItem>
                            ))}
                          </Select>
                        )}
                      </StyledTableCell>
                      <StyledTableCell sx={{ fontWeight: 700 }}>
                        {editcomm !== item.commitionId ? (
                          `${item.companyCommition}%`
                        ) : (
                          <TextField
                            fullWidth
                            size="small"
                            type="number"
                            value={autosugval.companyCommition}
                            placeholder="Commission %"
                            onChange={(e) => {
                              setAutosugval({
                                ...autosugval,
                                companyCommition: e.target.value
                              });
                            }}
                          />
                        )}
                      </StyledTableCell>
                      <StyledTableCell sx={{ fontWeight: 700, color: '#13519d' }}>
                        {editcomm !== item.commitionId ? (
                          `${item.partnerCommition}%`
                        ) : (
                          <TextField
                            fullWidth
                            size="small"
                            type="number"
                            placeholder="Share %"
                            value={autosugval.partnerCommition}
                            onChange={(e) => {
                              setAutosugval({
                                ...autosugval,
                                partnerCommition: e.target.value
                              });
                            }}
                          />
                        )}
                      </StyledTableCell>
                      <StyledTableCell>N/A</StyledTableCell>
                      <StyledTableCell align="center">
                        {editcomm !== item.commitionId ? (
                          <Tooltip title="Edit" arrow>
                            <IconButton
                              onClick={() => editcommission(item)}
                              sx={{
                                color: '#13519d',
                                backgroundColor: 'rgba(19, 81, 157, 0.08)',
                                borderRadius: '8px',
                                p: 0.75,
                                '&:hover': { backgroundColor: 'rgba(19, 81, 157, 0.16)' }
                              }}
                            >
                              <MdModeEdit size={16} />
                            </IconButton>
                          </Tooltip>
                        ) : (
                          <Stack direction="row" spacing={0.75} justifyContent="center">
                            <Tooltip title="Cancel" arrow>
                              <IconButton
                                onClick={() => removecommission()}
                                sx={{
                                  color: '#ef4444',
                                  backgroundColor: 'rgba(239, 68, 68, 0.08)',
                                  borderRadius: '8px',
                                  p: 0.75,
                                  '&:hover': { backgroundColor: 'rgba(239, 68, 68, 0.16)' }
                                }}
                              >
                                <AiFillCloseCircle size={16} />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Update" arrow>
                              <IconButton
                                onClick={() => firedApiPartnerUpdate(item.commitionId, index)}
                                sx={{
                                  color: '#16a34a',
                                  backgroundColor: 'rgba(22, 163, 74, 0.08)',
                                  borderRadius: '8px',
                                  p: 0.75,
                                  '&:hover': { backgroundColor: 'rgba(22, 163, 74, 0.16)' }
                                }}
                              >
                                <MdOutlineUpdate size={16} />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        )}
                      </StyledTableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Card>
        </Box>
      </Container>
    </Page>
  );
}

export default SetCommissionOnBoarding;
